import * as React from 'react';
import { shallowEqualArrays } from 'shallow-equal';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    canPlanMigrateToNewPricing,
    checkIsEnterpriseProductKey,
    findRequiredPlanForFeature,
    getBlockedFeature,
    type PricingFeatureKey,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { type BadgeProps, toast } from '@gitbook/spine-react';
import { useStable } from '@gitbook/util-react-hooks';

import { useNavigation } from '../../navigation';
import { apiClient } from '../api';
import { useCurrentOrganization } from '../organization';
import type { UpgradeAction } from './types';
import { useNavigateToUpgradeScreen } from './useNavigateToUpgradeScreen';
import { useTrial } from './useTrial';
import type { UseUpgradeSite } from './useUpgradeSite';
import { mapUpgradeActionKindToBadgeKind } from './useUpgradeUtils';

/**
 * We want to avoid the user spamming the admin to upgrade.
 */
const alreadyNotifiedOrgs = new Set<string>();

/**
 * Return an action to append to a button if an upgrade is required to use a feature.
 * Note that this hook will fail if the user is not an active member of the org.
 *
 * If `inputFeatureKeys` is an array, it'll require an upgrade action if at least one of the feature
 * requires an upgrade.
 */
export function useUpgrade(
    inputFeatureKeys: PricingFeatureKey | PricingFeatureKey[],
    extra?: { skip?: boolean }
): UpgradeAction | undefined {
    const featureKeys = Array.isArray(inputFeatureKeys) ? inputFeatureKeys : [inputFeatureKeys];
    const { navigate } = useNavigation();
    const navigateToUpgradeScreen = useNavigateToUpgradeScreen();
    const skip = extra?.skip ?? false;
    const organization = useCurrentOrganization();
    const canUpgrade = organization.permissions.admin;
    const stableFeatureKeys = useStable<PricingFeatureKey[]>(featureKeys, shallowEqualArrays);
    const trial = useTrial({ organization });
    const blockedFeature = React.useMemo(
        () => getBlockedFeature(stableFeatureKeys, organization.plan),
        [stableFeatureKeys, organization.plan]
    );

    return React.useMemo<UpgradeAction | undefined>(() => {
        if (!blockedFeature || skip) {
            return undefined;
        }

        // Check whether this is a self-serve migrate CTA.
        const isSelfServeMigrateCTA =
            blockedFeature && canPlanMigrateToNewPricing(organization.plan);

        const requiredPlanDefinition = findRequiredPlanForFeature(
            organization.plan,
            blockedFeature
        );
        const requiredPlanTitle = requiredPlanDefinition?.title;

        // If the required plan is Enterprise, let them get in touch with sales.
        if (requiredPlanDefinition && checkIsEnterpriseProductKey(requiredPlanDefinition.key)) {
            return {
                _type: 'contact-sales',
                label: 'Contact us',
                plan: requiredPlanTitle,
                kind: 'primary',
                leadingIcon: Icon.Send,
                href: 'mailto:csm@gitbook.com',
                target: '_blank',
                onPress: async () => {
                    analytics.logEvent(
                        analytics.createOrgEvent(organization, {
                            type: 'organization_open_enterprise_cta',
                            currentFeaturesSet: organization.plan,
                            reason: blockedFeature,
                        })
                    );
                },
            };
        }

        // If a trial is available, allow users to start a trial right away.
        if (trial.status === api.BillingTrialStatus.None && canUpgrade) {
            return {
                _type: 'upgrade',
                tooltip: {
                    label: 'Start a 14-day free trial',
                },
                label: 'Start trial',
                badgeLabel: 'Start trial',
                plan: requiredPlanTitle,
                onPress: async () => {
                    await toast.catch(async () => {
                        await apiClient.billing.upgradeOrganizationPlan(organization.id, {
                            product: api.BillingProduct.Pro2024,
                            interval: api.BillingInterval.Monthly,
                            reason: 'upgrade_cta_trial',
                        });
                        toast('Trial started', {
                            description: 'Your 14-day free trial has been activated.',
                        });
                    }, 'Error while starting trial');
                },
                kind: 'upgrade',
                leadingIcon: Icon.Upgrade,
            };
        }

        if (!canUpgrade) {
            return {
                _type: 'upgrade',
                tooltip: {
                    label: 'Notify and ask your administrator to upgrade this organization',
                },
                label: `Request upgrade to ${isSelfServeMigrateCTA ? 'new pricing' : requiredPlanTitle}`,
                badgeLabel: isSelfServeMigrateCTA ? 'Upgrade' : requiredPlanTitle,
                plan: isSelfServeMigrateCTA ? 'New pricing' : requiredPlanTitle,
                onPress: async () => {
                    if (alreadyNotifiedOrgs.has(organization.id)) {
                        toast('Upgrade already requested', {
                            description: `We've already notified the organization administrator to ask them to upgrade the account.`,
                        });
                        return;
                    }

                    alreadyNotifiedOrgs.add(organization.id);
                    analytics.logEvent(
                        analytics.createOrgEvent(organization, {
                            type: 'organization_request_upgrade',
                            currentFeaturesSet: organization.plan,
                            reason: blockedFeature,
                        })
                    );

                    await toast.catch(async () => {
                        await apiClient.organizations.requestOrganizationUpgrade(organization.id, {
                            featureKey: blockedFeature,
                        });
                        toast('Upgrade requested', {
                            description: `We've notified the organization administrator to ask them to upgrade the account.`,
                        });
                    }, 'Error while requesting upgrade');
                },
                kind: 'upgrade',
                leadingIcon: Icon.Upgrade,
            };
        }

        return {
            _type: 'upgrade',
            tooltip: {
                label: 'Upgrade to unlock this feature',
            },
            label: `Upgrade to ${isSelfServeMigrateCTA ? 'new pricing' : requiredPlanTitle}`,
            badgeLabel: isSelfServeMigrateCTA ? 'Upgrade' : requiredPlanTitle,
            plan: isSelfServeMigrateCTA ? 'New pricing' : requiredPlanTitle,
            onPress: async () => {
                if (isSelfServeMigrateCTA) {
                    navigate({
                        overlay: {
                            type: 'org_settings',
                            organization: organization.id,
                            tab: { type: 'upgrade' },
                            hash: blockedFeature,
                        },
                    });
                    return;
                }

                analytics.logEvent(
                    analytics.createOrgEvent(organization, {
                        type: 'organization_open_upgrade',
                        currentFeaturesSet: organization.plan,
                        reason: blockedFeature,
                    })
                );

                navigateToUpgradeScreen({
                    organizationId: organization.id,
                    reason: blockedFeature,
                });
            },
            kind: 'upgrade',
            leadingIcon: Icon.Upgrade,
        };
    }, [
        organization,
        blockedFeature,
        navigate,
        navigateToUpgradeScreen,
        canUpgrade,
        skip,
        trial.status,
    ]);
}

/**
 * Given an UpgradeAction it generates a set of props for an upgrade Badge
 */
export function getBadgePropsFromUpgradeAction(options: {
    upgrade: UpgradeAction | UseUpgradeSite;
}): BadgeProps {
    return {
        kind: mapUpgradeActionKindToBadgeKind(options.upgrade.kind),
        size: 'small',
        icon: options.upgrade.leadingIcon,
        label: options.upgrade.badgeLabel ?? options.upgrade.label,
        thin: true,
    };
}
